import { ConfigValues, DataInput, DataInputType } from '@/types/automationFields.ts';
import { AutomationStep, ControlBlockingMode } from '@/types/automationSteps.ts';
import { getAllStepChildren, isStepWithChildren } from '@/utils/stepUtils.ts';

export type DeleteImpactTarget =
  | { type: 'step'; stepId: string }
  | { type: 'static_dataset'; staticDatasetId: string }
  | { type: 'linked_dataset'; linkedDatasetId: string };

export type DeleteImpact = {
  stepId: string;
  stepName: string;
  reasons: string[];
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isDataInput = (value: unknown): value is DataInput => {
  if (!isObject(value)) return false;
  if (!('data_input_type' in value) || !('config' in value)) return false;
  if (!isObject(value.config)) return false;
  return Object.values(DataInputType).includes(value.data_input_type as DataInputType);
};

const isConfigValues = (value: unknown): value is ConfigValues => {
  if (!isObject(value)) return false;
  return Array.isArray(value.fields);
};

const collectStepAndDescendantIds = (steps: AutomationStep[], stepId: string): Set<string> => {
  const deletedStepIds = new Set<string>();

  const visit = (step: AutomationStep): boolean => {
    if (step.step_id === stepId) {
      collectAll(step);
      return true;
    }

    if (isStepWithChildren(step)) {
      return getAllStepChildren(step).some(visit);
    }

    return false;
  };

  const collectAll = (step: AutomationStep) => {
    deletedStepIds.add(step.step_id);
    if (isStepWithChildren(step)) {
      getAllStepChildren(step).forEach(collectAll);
    }
  };

  steps.some(visit);
  return deletedStepIds;
};

const dataInputReferencesTarget = (
  dataInput: DataInput,
  target: DeleteImpactTarget,
  deletedStepIds: Set<string>,
): string | null => {
  if (dataInput.data_input_type === DataInputType.Dynamic) {
    const { config } = dataInput;
    if (!('source_step_id' in config)) return null;
    const { source_step_id, output_id } = config;
    if (target.type === 'step' && source_step_id && deletedStepIds.has(source_step_id)) {
      return output_id ? 'uses an output from this step' : 'uses this step as an input';
    }
  }

  if (
    target.type === 'static_dataset' &&
    dataInput.data_input_type === DataInputType.Static &&
    'static_dataset_id' in dataInput.config &&
    dataInput.config.static_dataset_id === target.staticDatasetId
  ) {
    return 'uses this data source';
  }

  if (
    target.type === 'linked_dataset' &&
    dataInput.data_input_type === DataInputType.Linked &&
    'linked_dataset_id' in dataInput.config &&
    dataInput.config.linked_dataset_id === target.linkedDatasetId
  ) {
    return 'uses this linked data source';
  }

  return null;
};

const controlDependencyReferencesTarget = (
  value: unknown,
  target: DeleteImpactTarget,
  deletedStepIds: Set<string>,
): string | null => {
  if (target.type !== 'step' || !isObject(value)) return null;

  const waitForStepIds = value.wait_for_step_ids;
  if (
    Array.isArray(waitForStepIds) &&
    waitForStepIds.some((stepId) => typeof stepId === 'string' && deletedStepIds.has(stepId))
  ) {
    return 'waits for this step';
  }

  const blockingMode = value.blocking_mode;
  const blockingStepIds = value.blocking_step_ids;
  if (
    blockingMode === ControlBlockingMode.SelectedSteps &&
    Array.isArray(blockingStepIds) &&
    blockingStepIds.some((stepId) => typeof stepId === 'string' && deletedStepIds.has(stepId))
  ) {
    return 'gates this step';
  }

  return null;
};

const recoveryCheckpointReferencesTarget = (
  value: unknown,
  target: DeleteImpactTarget,
  deletedStepIds: Set<string>,
): string | null => {
  if (target.type !== 'step' || !isObject(value) || !Array.isArray(value.recovery_checkpoints)) return null;
  if (
    value.recovery_checkpoints.some(
      (checkpoint) =>
        isObject(checkpoint) &&
        typeof checkpoint.source_step_id === 'string' &&
        deletedStepIds.has(checkpoint.source_step_id),
    )
  ) {
    return 'uses this step as a recovery checkpoint';
  }
  return null;
};

const collectReferenceReasons = (
  value: unknown,
  target: DeleteImpactTarget,
  deletedStepIds: Set<string>,
): string[] => {
  const reasons = new Set<string>();
  const seen = new Set<unknown>();

  const visit = (node: unknown) => {
    if (!isObject(node) || seen.has(node)) return;
    seen.add(node);

    const controlDependencyReason = controlDependencyReferencesTarget(node, target, deletedStepIds);
    if (controlDependencyReason) reasons.add(controlDependencyReason);

    const recoveryCheckpointReason = recoveryCheckpointReferencesTarget(node, target, deletedStepIds);
    if (recoveryCheckpointReason) reasons.add(recoveryCheckpointReason);

    if (isDataInput(node)) {
      const reason = dataInputReferencesTarget(node, target, deletedStepIds);
      if (reason) reasons.add(reason);
    }

    if (isConfigValues(node)) {
      node.fields.forEach((field) => {
        if (!field.is_hidden && !field.is_disabled) {
          visit(field.value);
        }
      });
      return;
    }

    Object.values(node).forEach(visit);
  };

  visit(value);
  return [...reasons];
};

export const collectDeleteImpacts = (
  steps: AutomationStep[] | undefined,
  target: DeleteImpactTarget,
): DeleteImpact[] => {
  if (!steps?.length) return [];

  const deletedStepIds =
    target.type === 'step' ? collectStepAndDescendantIds(steps, target.stepId) : new Set<string>();
  const impacts: DeleteImpact[] = [];

  const visit = (step: AutomationStep) => {
    if (deletedStepIds.has(step.step_id)) {
      return;
    }

    const reasons = collectReferenceReasons(step, target, deletedStepIds);
    if (reasons.length > 0) {
      impacts.push({
        stepId: step.step_id,
        stepName: step.name || 'Untitled step',
        reasons,
      });
    }

    if (isStepWithChildren(step)) {
      getAllStepChildren(step).forEach(visit);
    }
  };

  steps.forEach(visit);
  return impacts;
};
